import React from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Col, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import useStripeConnect from '../../hooks/useStripeConnect'

export default function StripeConnectSection({
  entityId,
  translationPrefix,
  getAccountStatus,
  createStripeAccount,
  getOnboardingLink,
  returnUrl,
  refreshUrl,
  isComplete,
  getBadgeStatusKey,
  statusVariants,
  onVerificationComplete,
  onConnectClick,
  showAccountDetails = true,
  enabled = true,
  prerequisiteMessage = null,
  bare = false,
}) {
  const { t } = useTranslation()
  const {
    connecting,
    error,
    handleConnect,
    loadingStatus,
    polling,
    setError,
    setStripeMessage,
    stripeMessage,
    stripeStatus,
  } = useStripeConnect({
    createStripeAccount,
    enabled,
    entityId,
    getAccountStatus,
    getOnboardingLink,
    isComplete,
    onVerificationComplete,
    refreshUrl,
    returnUrl,
  })

  if (!entityId || !enabled) {
    return null
  }

  const isInitialLoading = stripeStatus === null && loadingStatus
  const isNotCreated = !stripeStatus?.has_stripe_account
  const badgeKey = getBadgeStatusKey(stripeStatus)
  const badgeVariant = statusVariants[badgeKey] || statusVariants.not_created
  const complete = isComplete(stripeStatus)

  const handleConnectClick = () => {
    if (prerequisiteMessage) {
      return
    }
    if (onConnectClick) {
      onConnectClick(handleConnect)
      return
    }
    handleConnect()
  }

  const getActionButton = () => {
    if (complete) {
      return null
    }

    const buttonVariant = isNotCreated ? 'primary' : 'warning'
    const labelKey = isNotCreated ? 'connectButton' : 'completeSetupButton'
    const loadingLabelKey = isNotCreated ? 'connectingButton' : 'loadingButton'

    return (
      <Button
        variant={buttonVariant}
        onClick={handleConnectClick}
        disabled={connecting || polling || !!prerequisiteMessage}
      >
        {connecting ? (
          <>
            <Spinner size="sm" className="me-2" />
            {t(`${translationPrefix}.${loadingLabelKey}`)}
          </>
        ) : (
          t(`${translationPrefix}.${labelKey}`)
        )}
      </Button>
    )
  }

  return (
    <div className={bare ? undefined : 'container mt-4'}>
      {!bare && (
        <Row className="align-items-center">
          <Col>
            <h3 className="text-start mb-0">
              {t(`${translationPrefix}.title`)}
            </h3>
          </Col>
        </Row>
      )}

      {stripeMessage && (
        <Row className="mt-3">
          <Col>
            <Alert
              variant={stripeMessage.type}
              onClose={() => setStripeMessage(null)}
              dismissible
            >
              {t(`${translationPrefix}.messages.${stripeMessage.key}`)}
            </Alert>
          </Col>
        </Row>
      )}

      {prerequisiteMessage && (
        <Row className="mt-3">
          <Col>
            <Alert variant="warning">{prerequisiteMessage}</Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mt-3">
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {polling && (
        <Row className="mt-3">
          <Col>
            <Alert variant="info">
              <Spinner size="sm" className="me-2" />
              {t(`${translationPrefix}.checkingVerification`)}
            </Alert>
          </Col>
        </Row>
      )}

      {isInitialLoading ? (
        <Row className="mt-3 p-3 bg-light rounded">
          <Col className="d-flex align-items-center justify-content-center">
            <Spinner size="sm" className="me-2" />
            <span className="text-muted">
              {t(`${translationPrefix}.loadingStatus`)}
            </span>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="mt-3 p-3 bg-light rounded">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div>
                  <strong>{t(`${translationPrefix}.connect`)}</strong>
                  <p className="text-muted mb-0 small">
                    {complete
                      ? t(`${translationPrefix}.connectedDescription`)
                      : t(`${translationPrefix}.notConnectedDescription`)}
                  </p>
                </div>
              </div>
            </Col>
            <Col
              md={4}
              className="d-flex align-items-center justify-content-end gap-2"
            >
              <span className={`badge bg-${badgeVariant}`}>
                {t(`${translationPrefix}.status.${badgeKey}`)}
              </span>
              {getActionButton()}
            </Col>
          </Row>

          {showAccountDetails && stripeStatus?.has_stripe_account && (
            <Row className="mt-2">
              <Col>
                <small className="text-muted">
                  <strong>{t(`${translationPrefix}.payoutsEnabled`)}</strong>{' '}
                  {stripeStatus.stripe_payouts_enabled
                    ? t(`${translationPrefix}.yes`)
                    : t(`${translationPrefix}.no`)}
                  {' | '}
                  <strong>
                    {t(`${translationPrefix}.detailsSubmitted`)}
                  </strong>{' '}
                  {stripeStatus.stripe_details_submitted
                    ? t(`${translationPrefix}.yes`)
                    : t(`${translationPrefix}.no`)}
                </small>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  )
}

StripeConnectSection.propTypes = {
  createStripeAccount: PropTypes.func.isRequired,
  enabled: PropTypes.bool,
  entityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  getAccountStatus: PropTypes.func.isRequired,
  getBadgeStatusKey: PropTypes.func.isRequired,
  getOnboardingLink: PropTypes.func.isRequired,
  isComplete: PropTypes.func.isRequired,
  onConnectClick: PropTypes.func,
  onVerificationComplete: PropTypes.func,
  prerequisiteMessage: PropTypes.string,
  refreshUrl: PropTypes.string.isRequired,
  returnUrl: PropTypes.string.isRequired,
  showAccountDetails: PropTypes.bool,
  statusVariants: PropTypes.object.isRequired,
  translationPrefix: PropTypes.string.isRequired,
  bare: PropTypes.bool,
}
